import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  ListSubheader,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import type { TemplateCustomerContext } from '../../types/ticketTypeTemplate';
import type { RenewalScheduleItem } from '@techgitter/contracts';
import type { CatalogProduct } from '@techgitter/contracts';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import {
  skuMatchesCategory,
  type LicenseCatalogCategory,
} from '../../data/licenseCategories';

export type LicenseTerm = 'monthly' | 'annual';

interface LicenseOption {
  label: string;
  value: string;       // product SKU
  group: string;
  seatCount?: number;
  isExisting: boolean;
}

interface LicensePickerProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  catalogCategory: LicenseCatalogCategory;
  /** 'all' (default) shows existing + catalog; 'existing-only' restricts to current licenses. */
  mode?: 'all' | 'existing-only';
  value: string;
  onChange: (sku: string) => void;
  onTermChange?: (term: LicenseTerm) => void;
  customerContext?: TemplateCustomerContext;
  error?: string;
}

export const LicensePicker: React.FC<LicensePickerProps> = ({
  label,
  placeholder,
  required = false,
  catalogCategory,
  mode = 'all',
  value,
  onChange,
  onTermChange,
  customerContext,
  error,
}) => {
  const { products } = useProductCatalog();
  const [selectedTerm, setSelectedTerm] = useState<LicenseTerm>('monthly');

  const options = useMemo<LicenseOption[]>(() => {
    const scheduleItems: RenewalScheduleItem[] = customerContext?.renewalSchedule ?? [];
    const existingSkus = new Set<string>();

    // Group 1: Customer's existing licenses from renewal schedule
    // Sorted by seat count descending
    const existingOptions: LicenseOption[] = [];
    scheduleItems
      .filter(item => skuMatchesCategory(item.productSku, catalogCategory))
      .forEach(item => {
        existingSkus.add(item.productSku);
        existingOptions.push({
          label: item.productName,
          value: item.productSku,
          group: "Customer's existing licenses",
          seatCount: Math.round(Number(item.quantity)),
          isExisting: true,
        });
      });

    // Sort existing licenses by seat count descending
    existingOptions.sort((a, b) => (b.seatCount ?? 0) - (a.seatCount ?? 0));

    if (mode === 'existing-only') {
      return existingOptions;
    }

    // Group 2: Full catalog (excluding items already in Group 1)
    // Sorted alphabetically by name. Licenses are identified by SKU prefix
    // (see data/licenseCategories.ts) rather than the product's catalog
    // category, since the catalog uses generic categories like "other".
    const catalogOptions: LicenseOption[] = products
      .filter((p: CatalogProduct) => skuMatchesCategory(p.sku, catalogCategory) && !p.disabled)
      .filter((p: CatalogProduct) => !existingSkus.has(p.sku))
      .map((p: CatalogProduct) => ({
        label: p.displayName,
        value: p.sku,
        group: 'All licenses',
        isExisting: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...existingOptions, ...catalogOptions];
  }, [products, customerContext, catalogCategory, mode]);

  const selectedOption = options.find(o => o.value === value) || null;
  const showTermToggle = selectedOption != null && !selectedOption.isExisting;

  const handleTermChange = (_: React.MouseEvent<HTMLElement>, newTerm: LicenseTerm | null) => {
    if (!newTerm) return; // prevent deselecting
    setSelectedTerm(newTerm);
    onTermChange?.(newTerm);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Autocomplete
        fullWidth
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange(newValue?.value ?? '');
        }}
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            required={required}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                ...params.InputProps,
                'aria-label': label,
                'aria-required': required,
              } as any,
            }}
          />
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader
              component="div"
              sx={{
                bgcolor: 'grey.100',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: 'text.primary',
                lineHeight: '36px',
              }}
            >
              {params.group}
            </ListSubheader>
            {params.children}
          </li>
        )}
        renderOption={(props, option) => {
          const { key, ...restProps } = props as any;
          return (
            <Box component="li" key={key} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {option.label}
                </Typography>
                {option.seatCount != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    x {option.seatCount}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />

      {showTermToggle && (
        <ToggleButtonGroup
          value={selectedTerm}
          exclusive
          onChange={handleTermChange}
          size="small"
          aria-label="License term"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="annual">Annual</ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );
};
